import RefundModalOverviewFooter from '@/components/manage/refund/RefundModalOverviewFooter';
import RefundModalSelectionFooter from '@/components/manage/refund/RefundModalSelectionFooter';
import { Typography } from '@/components/primitives/Typography';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import { manageBookingRefundLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  confirmRefundOffer,
  deleteRefundOffer,
  getManageFlowBookingById,
  resetRefundSelection,
} from '@/features/manage/manageActions';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import { type FC, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

interface RefundModalProps {
  isOpen: boolean;
  refundSelection: FC;
  refundOverview: FC;
  selected: number;
  chooseToRefundText: 'chooseTripsToRefund' | 'chooseTravelPassesToRefund';
  refundText: 'refundTrips' | 'refundTravelPasses';
  onInitiateRefund: () => Promise<void>;
  onClose: () => void;
}

enum RefundStep {
  selection,
  confirmation,
}

const RefundModal: FC<RefundModalProps> = ({
  isOpen,
  refundSelection: RefundSelection,
  refundOverview: RefundOverview,
  selected,
  chooseToRefundText,
  refundText,
  onInitiateRefund,
  onClose,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector(manageBookingRefundLoadingSelector);
  const [currentStep, setCurrentStep] = useState<RefundStep>(
    RefundStep.selection
  );
  const currentBooking = useSelector(manageFlowBookingSelector);

  const handleClose = useCallback(() => {
    onClose();
    dispatch(resetRefundSelection());
    setCurrentStep(RefundStep.selection);
  }, [dispatch, onClose]);

  const handleOpenChange = useCallback(
    async (open: boolean) => {
      if (!open && currentStep === RefundStep.confirmation) {
        await dispatch(deleteRefundOffer()).unwrap();
      }

      handleClose();
    },
    [currentStep, dispatch, handleClose]
  );

  const handleBackToSelection = useCallback(async () => {
    await dispatch(deleteRefundOffer()).unwrap();
    setCurrentStep(RefundStep.selection);
  }, [dispatch]);

  const handleInitiateRefund = useCallback(async () => {
    await onInitiateRefund();
    setCurrentStep(RefundStep.confirmation);
  }, [onInitiateRefund]);

  const handleConfirmRefund = useCallback(async () => {
    await dispatch(confirmRefundOffer()).unwrap();
    await dispatch(getManageFlowBookingById(currentBooking?.id)).unwrap();

    toast.success(<TransText i18nKey="refundInitiated" />);
    handleClose();
  }, [currentBooking, dispatch, handleClose]);

  const { title, content, footer } = useMemo(() => {
    if (currentStep === RefundStep.selection) {
      return {
        title: (
          <Typography variant="subtitle">
            <TransText i18nKey={chooseToRefundText} />
          </Typography>
        ),
        content: <RefundSelection />,
        footer: (
          <RefundModalSelectionFooter
            handleClose={handleClose}
            handleInitiateRefund={handleInitiateRefund}
            selected={selected}
            loading={loading}
          />
        ),
      };
    }

    return {
      title: (
        <Typography variant="subtitle">
          <TransText i18nKey={refundText} values={{ count: selected }} />
        </Typography>
      ),
      content: <RefundOverview />,
      footer: (
        <RefundModalOverviewFooter
          handleConfirmRefund={handleConfirmRefund}
          handleBackToSelection={handleBackToSelection}
          loading={loading}
        />
      ),
    };
  }, [
    RefundOverview,
    RefundSelection,
    chooseToRefundText,
    currentStep,
    handleBackToSelection,
    handleClose,
    handleConfirmRefund,
    handleInitiateRefund,
    loading,
    refundText,
    selected,
  ]);

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      loading={loading}
      title={title}
      content={content}
      footer={footer}
    />
  );
};

export default RefundModal;
